import { trapFocus, removeTrapFocus } from '@theme/focus';

class HyghDrawer extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  /** @type {HTMLElement | null} */
  #returnFocusTo = null;

  connectedCallback() {
    this.setAttribute('role', 'dialog');
    const title = this.querySelector('.hygh-drawer__title');
    if (title) {
      if (!title.id) title.id = `${this.id}-title`;
      this.setAttribute('aria-labelledby', title.id);
    }
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    this.#returnFocusTo = /** @type {HTMLElement | null} */ (document.activeElement);
    this.setAttribute('open', '');
    // Keep Tab inside the open drawer, same helper as Horizon's mobile nav
    trapFocus(this);
    if (!this.contains(document.activeElement)) {
      /** @type {HTMLElement | null} */ (this.querySelector('.hygh-drawer__close'))?.focus();
    }
  }

  close() {
    const hadFocus = this.contains(document.activeElement);
    this.removeAttribute('open');
    removeTrapFocus();
    if (hadFocus) this.#returnFocusTo?.focus();
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'open' || oldValue === newValue) return;
    this.dispatchEvent(new CustomEvent(newValue === null ? 'closed' : 'opened'));
  }
}

customElements.define('hygh-drawer', HyghDrawer);

// Delegated trigger handling: any element with data-hygh-drawer="<id>" toggles
// the <hygh-drawer id="<id>">. Single listener covers any number of drawers.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-hygh-drawer]');
  if (trigger) {
    document.getElementById(trigger.dataset.hyghDrawer)?.toggle();
    return;
  }

  document.querySelectorAll('hygh-drawer[open][hide-on-blur]').forEach((drawer) => {
    if (!drawer.contains(event.target)) drawer.close();
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    const trigger = event.target.closest('[data-hygh-drawer][role="button"]');
    if (trigger) {
      event.preventDefault();
      trigger.click();
    }
    return;
  }

  if (event.key !== 'Escape') return;
  document.querySelectorAll('hygh-drawer[open]').forEach((drawer) => drawer.close());
});


class KlaviyoNotify extends HTMLElement {
  constructor() {
    super();
  }
  open() {
    this.classList.add("active");
  }
  close() {
    this.classList.remove("active");
  }
  successCallback(result) {
    const content = this.querySelector(".klaviyo-content");
    const success = this.querySelector(".klaviyo-success");
    content.classList.add("hidden");
    success.classList.remove("hidden");
  }
  errorCallback(error) {
    const content = this.querySelector(".klaviyo-content");
    const error_display = this.querySelector(".klaviyo-error");
    content.classList.add("hidden");
    error_display.classList.remove("hidden");
  }
  submit(email) {
    const that = this;
    const variant = this.querySelector('.bis-variant.selected')?.getAttribute('variant');
    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: `${email}`,
              },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variant}`,
            },
          },
        },
      },
    };
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: "2026-07-15",
      },
      body: JSON.stringify(payload),
    };
    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`, options)
      .then((result) => that.successCallback(result))
      .catch((error) => that.errorCallback(error));
  }
  connectedCallback() {
    this.emailInput = this.querySelector("#klaviyo-email-input");
    this.submitButton = this.querySelector(".klaviyo-email-submit");
    this.content = this.querySelector(".klaviyo-popup-card");
    this.fieldError = this.querySelector(".klaviyo-field-error");
    this.companyId = this.dataset.klaviyoCompanyId;

    if (!!this.emailInput && !!this.submitButton) {
      this.submitButton.addEventListener("click", (event) => {
        event.preventDefault();
        if (this.emailInput.validity.valid) {
          this.fieldError?.classList.add("hidden");
          this.emailInput.removeAttribute("aria-invalid");
          const email = this.emailInput.value;
          if (!!email) {
            this.submit(email);
          }
        } else {
          this.fieldError?.classList.remove("hidden");
          this.emailInput.setAttribute("aria-invalid", "true");
        }
      });
      this.emailInput.addEventListener("input", () => {
        this.fieldError?.classList.add("hidden");
        this.emailInput.removeAttribute("aria-invalid");
      });
    }
  }
}
if(!customElements.get("klaviyo-notify")) {
  customElements.define("klaviyo-notify", KlaviyoNotify);
}


class KlaviyoSubscribe extends HTMLElement {
  /**
   * @param {"success" | "error"} type
   * @param {string} text
   */
  showMessage(type, text) {
    if (!this.messageEl) return;
    this.messageEl.className = `klaviyo-status-message ${type}`;
    this.messageEl.textContent = text;
    this.messageEl.style.display = "block";
    this.emailInput?.setAttribute("aria-invalid", String(type === "error"));
  }
  submit(email) {
    const that = this;
    const listId = this.dataset.listId;
    const companyId = this.dataset.companyId;

    if (!listId || !companyId) {
      this.showMessage("error", "Klaviyo List ID or Public API Key is missing in section settings.");
      return;
    }

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: `${email}`,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: listId,
            },
          },
        },
      },
    };
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: "2026-07-15",
      },
      body: JSON.stringify(payload),
    };
    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, options)
      .then((response) => {
        if (response.ok || response.status === 202) {
          that.showMessage("success", that.dataset.successMessage || "");
          that.form?.reset();
        } else {
          that.showMessage("error", that.dataset.errorMessage || "");
        }
      })
      .catch(() => that.showMessage("error", that.dataset.errorMessage || ""));
  }
  connectedCallback() {
    this.form = this.querySelector("form");
    this.emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="email"]'));
    this.messageEl = /** @type {HTMLElement | null} */ (this.querySelector(".klaviyo-status-message"));

    if (this.form) {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (this.emailInput?.value) {
          this.submit(this.emailInput.value);
        }
      });
    }
  }
}
if (!customElements.get("klaviyo-subscribe")) {
  customElements.define("klaviyo-subscribe", KlaviyoSubscribe);
}